import React, { useEffect, useState } from "react";
import { Modal, Button } from "components/common";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";
import apiAxios from "helpers/apiAxios";
import { createFeatureFlag, updateFeatureFlag } from "redux/authorization/featureFlagSlice";
import { FaBan, FaSave } from "react-icons/fa";
import PropTypes from 'prop-types';

import { INPUT_CLS as inputCls } from 'helpers/formStyles';
const selectStyles = {
  control: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    minHeight: '38px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#60a5fa' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(96,165,250,0.2)' : 'none',
    '&:hover': { borderColor: '#60a5fa' },
  }),
  groupHeading: (base) => ({
    ...base, fontSize: '0.7rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.8rem',
    padding: '6px 10px',
    color: state.isSelected ? '#1e293b' : '#334155',
    backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#eff6ff' : 'white',
    cursor: 'pointer',
  }),
  placeholder: base => ({ ...base, fontSize: '0.875rem', color: '#cbd5e1' }),
  menuList: (base) => ({ ...base, maxHeight: '240px' }),
  // Render menu above the modal (zIndex must exceed the Modal's own 100000).
  menuPortal: (base) => ({ ...base, zIndex: 1000001 }),
};

// stage → who can see the feature (matches Permission::User#resolve_permission).
const STAGE_OPTIONS = [
  { value: "hidden",   label: "Hidden — nobody (incl. admin)" },
  { value: "internal", label: "Internal — admins only (dogfood)" },
  { value: "released", label: "Released — everyone" },
];

// Flatten grouped options to find the selected one by code value.
const findOption = (groups, value) => {
  for (const g of groups) {
    const found = g.options.find((o) => o.value === value);
    if (found) return found;
  }
  return null;
};

// Render each option as a monospace code badge + description on one line.
const formatOptionLabel = (opt, { context }) => {
  if (opt.__isNew__) return <span>{opt.label}</span>;
  const code = opt.code || opt.value;
  if (context === "value") {
    return <span>{code}{opt.name ? ` — ${opt.name}` : ""}</span>;
  }
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
      <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-bg-primary-50 tw-text-primary-600 tw-text-xs tw-font-mono tw-font-semibold">
        {code}
      </span>
      <span className="tw-truncate tw-text-slate-600">{opt.name}</span>
    </div>
  );
};

const Field = ({ label, required, error, hint, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-1">
    <label className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">
      {label}{required && <span className="tw-text-red-400 tw-ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="tw-text-xs tw-text-slate-400 tw-mt-0.5">{hint}</p>}
    {error && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5">{error}</p>}
  </div>
);

Field.propTypes = {
  label:    PropTypes.string.isRequired,
  required: PropTypes.bool,
  error:    PropTypes.string,
  hint:     PropTypes.string,
  children: PropTypes.node.isRequired,
};

const FeatureFlagForm = ({ showModal, setshowModal, title, featureFlag }) => {
  const { register, control, handleSubmit, formState: { errors }, setValue, getValues, reset } = useForm({
    defaultValues: { stage: "hidden" }
  });
  const dispatch = useDispatch();
  const [codeOptions, setCodeOptions] = useState([]);

  // Load coded permissions grouped by module (group_name) so the Code picker
  // shows which module each code belongs to.
  useEffect(() => {
    if (!showModal) return undefined;
    let mounted = true;
    (async () => {
      try {
        const res = await apiAxios.get("/authorization/permissions/code_options");
        const data = res.data?.data || {};
        const groups = Object.entries(data).map(([mod, perms]) => ({
          label: (mod || "other").replace(/_/g, " ").toUpperCase(),
          options: (perms || []).map((p) => {
            const desc = p.description || `${p.subject_class} · ${p.action}`;
            // `label` drives react-select's search/filter; display is via formatOptionLabel.
            return { value: p.code, label: `${p.code} ${desc}`, code: p.code, name: desc, module: mod || "other" };
          }),
        }));
        if (mounted) setCodeOptions(groups);
      } catch (error) {
        // silent — Code can still be typed manually
      }
    })();
    return () => { mounted = false; };
  }, [showModal]);

  useEffect(() => {
    if (!featureFlag) reset({ code: "", name: "", stage: "hidden", description: "" });
  }, [title, featureFlag, reset]);

  useEffect(() => {
    if (featureFlag) {
      ["code", "name", "stage", "description"].forEach((key) => setValue(key, featureFlag[key] ?? ""));
    }
  }, [setValue, featureFlag]);

  const closeModal = () => setshowModal(false);

  const onSubmit = async (data) => {
    try {
      if (featureFlag) {
        dispatch(updateFeatureFlag({ featureFlagId: featureFlag.id, data }));
        closeModal();
      } else {
        dispatch(createFeatureFlag(data));
        reset({ code: "", name: "", stage: "hidden", description: "" });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title={title}
      size="md"
      backdropClick={false}
      footer={
        <div className="tw-flex tw-gap-2">
          <Button type="submit" form="feature-flag-form" size="sm" variant="primary">
            <FaSave size={12} /> Save
          </Button>
          <Button variant="danger" size="sm" onClick={closeModal}>
            <FaBan size={12} /> Cancel
          </Button>
        </div>
      }
    >
      <form id="feature-flag-form" onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-gap-4">

        {/* Code + Stage — side by side */}
        <Field label="Code" required error={errors.code?.message} hint="Pick a permission (grouped by module) or type a custom vXXX code.">
          <Controller
            control={control}
            name="code"
            rules={{ required: "Code is required." }}
            render={({ field }) => (
              <CreatableSelect
                options={codeOptions}
                value={findOption(codeOptions, field.value) || (field.value ? { value: field.value, code: field.value, name: "" } : null)}
                placeholder="Select or type a code…"
                classNamePrefix="react-select"
                styles={selectStyles}
                isClearable
                formatOptionLabel={formatOptionLabel}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                formatCreateLabel={(input) => `Use custom code "${input}"`}
                onChange={(opt) => {
                  setValue("code", opt ? opt.value : "", { shouldValidate: true });
                  // Prefill Name from the permission when Name is still empty.
                  if (opt?.name && !getValues("name")) {
                    setValue("name", opt.module ? `${opt.module} · ${opt.name}` : opt.name);
                  }
                }}
              />
            )}
          />
        </Field>

        <Field label="Stage" required error={errors.stage?.message}>
          <select className={inputCls} {...register("stage", { required: "Stage is required." })}>
            {STAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        {/* Name — full width */}
        <Field label="Name" required error={errors.name?.message}>
          <input
            type="text"
            placeholder="e.g. Procurement · Purchase Orders"
            className={inputCls}
            {...register("name", { required: "Name is required." })}
          />
        </Field>

        {/* Description — full width */}
        <Field label="Description" error={errors.description?.message}>
          <textarea
            rows={3}
            placeholder="Describe this feature and why it isn't released yet…"
            className={`${inputCls} tw-resize-none`}
            {...register("description")}
          />
        </Field>

      </form>
    </Modal>
  );
};

FeatureFlagForm.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  title:        PropTypes.string.isRequired,
  featureFlag:  PropTypes.object,
};

export default FeatureFlagForm;
